import { useState } from "react";
import { getMe, updateMe, USER_FIELD_MAX, type UpdateMeRequest, type User } from "@safers/api";
import { useAuthStore } from "./store";

export type ProfileField = keyof UpdateMeRequest;
export type ProfileErrors = Partial<Record<ProfileField, string>>;

const PROFILE_FIELDS: ProfileField[] = ["name", "department", "phoneNumber"];

const toValues = (user: User): UpdateMeRequest => ({
  name: user.name,
  department: user.department ?? "",
  phoneNumber: user.phoneNumber ?? "",
});

export const trimProfile = (values: UpdateMeRequest): UpdateMeRequest => ({
  name: values.name.trim(),
  department: values.department.trim(),
  phoneNumber: values.phoneNumber.trim(),
});

export function validateProfile(values: UpdateMeRequest): ProfileErrors {
  const errors: ProfileErrors = {};
  for (const field of PROFILE_FIELDS) {
    const max = USER_FIELD_MAX[field];
    if (values[field].length > max) errors[field] = `${max}자까지 입력할 수 있습니다.`;
  }
  if (!values.name) errors.name = "필수입니다.";
  return errors;
}

interface ProfileFormOptions {
  onSaved: () => void;
  onError: (err: unknown) => void;
}

export function useProfileForm(user: User, { onSaved, onError }: ProfileFormOptions) {
  const [values, setValues] = useState(() => toValues(user));
  const [errors, setErrors] = useState<ProfileErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const body = trimProfile(values);
  const current = toValues(user);
  const isDirty = PROFILE_FIELDS.some((field) => body[field] !== current[field]);

  function change(field: ProfileField, value: string) {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  async function submit() {
    const nextErrors = validateProfile(body);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSaving(true);
    try {
      await updateMe(body);
      const saved = await getMe().catch(() => ({ ...user, ...body }));
      useAuthStore.getState().setUser(saved);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setIsSaving(false);
    }
  }

  return { values, errors, isSaving, isDirty, change, submit };
}
